import type { AudioTransportState } from "../../shared/types.ts";
import type { RoleAuth } from "./auth.ts";

type SocketStatus = {
  audioTransport: AudioTransportState;
  audioTransportError: string | null;
};

type IngressSocket = Pick<WebSocket, "binaryType" | "readyState" | "send" | "close"> & {
  onopen: ((event: Event) => void) | null;
  onmessage: ((event: MessageEvent) => void) | null;
  onerror: ((event: Event) => void) | null;
  onclose: ((event: CloseEvent) => void) | null;
};

const SOCKET_CONNECTING = 0;
const SOCKET_OPEN = 1;
const DEFAULT_RETRY_BASE_MS = 250;
const DEFAULT_RETRY_MAX_MS = 5_000;
const FATAL_CLOSE_CODES = new Set([1002, 1003, 1007, 1008, 1009, 1010, 4000, 4001, 4003, 4401, 4403]);

export function createAudioIngressSocket(input: {
  ingressUrl: string;
  sessionId: string;
  auth: RoleAuth;
  sampleRate: number;
  createSocket?: (url: string) => IngressSocket;
  onStatus: (status: SocketStatus) => void;
  onMessage: (data: unknown) => void;
  onFatalError: (message: string) => void;
  retryBaseMs?: number;
  retryMaxMs?: number;
}) {
  const createSocket = input.createSocket ?? ((url: string) => new WebSocket(url));
  const retryBaseMs = input.retryBaseMs ?? DEFAULT_RETRY_BASE_MS;
  const retryMaxMs = input.retryMaxMs ?? DEFAULT_RETRY_MAX_MS;
  let socket: IngressSocket | null = null;
  let stopped = true;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectAttempt = 0;
  let lastErrorMessage: string | null = null;

  function start(): void {
    if (!stopped) return;
    stopped = false;
    reconnectAttempt = 0;
    connect(true);
  }

  function stop(): void {
    stopped = true;
    if (retryTimer) clearTimeout(retryTimer);
    retryTimer = null;
    const current = socket;
    socket = null;
    if (current?.readyState === SOCKET_OPEN) current.close(1000, "capture stopped");
    else if (current?.readyState === SOCKET_CONNECTING) current.close();
    input.onStatus({ audioTransport: "disconnected", audioTransportError: null });
  }

  function sendAudio(frame: ArrayBuffer): void {
    if (socket?.readyState === SOCKET_OPEN) socket.send(frame);
  }

  function connect(emitConnecting: boolean): void {
    if (stopped) return;
    if (emitConnecting) input.onStatus({ audioTransport: "connecting", audioTransportError: lastErrorMessage });
    const next = createSocket(input.ingressUrl);
    socket = next;
    next.binaryType = "arraybuffer";
    next.onopen = () => {
      if (stopped || socket !== next) return;
      reconnectAttempt = 0;
      lastErrorMessage = null;
      next.send(JSON.stringify({
        type: "config",
        sessionId: input.sessionId,
        auth: input.auth,
        sampleRate: input.sampleRate,
        encoding: "pcm16",
        channelCount: 1,
      }));
      input.onStatus({ audioTransport: "connected", audioTransportError: null });
    };
    next.onmessage = (event) => {
      if (socket !== next) return;
      input.onMessage(event.data);
    };
    next.onerror = () => {
      lastErrorMessage = "audio ingress connection failed";
    };
    next.onclose = (event) => {
      if (socket === next) socket = null;
      if (stopped) return;
      const message = closeMessage(event, lastErrorMessage);
      lastErrorMessage = message;
      if (isFatalClose(event.code)) {
        input.onStatus({ audioTransport: "error", audioTransportError: message });
        input.onFatalError(message);
        return;
      }
      scheduleReconnect(message);
    };
  }

  function scheduleReconnect(message: string): void {
    if (retryTimer) clearTimeout(retryTimer);
    reconnectAttempt += 1;
    const delayMs = Math.min(retryMaxMs, retryBaseMs * 2 ** (reconnectAttempt - 1));
    input.onStatus({ audioTransport: "connecting", audioTransportError: message });
    retryTimer = setTimeout(() => {
      retryTimer = null;
      connect(false);
    }, delayMs);
  }

  return {
    start,
    stop,
    sendAudio,
    isConnected: () => socket?.readyState === SOCKET_OPEN,
  };
}

function isFatalClose(code: number): boolean {
  return FATAL_CLOSE_CODES.has(code);
}

function closeMessage(event: CloseEvent, fallback: string | null): string {
  if (event.reason) return `audio ingress disconnected (${event.code}: ${event.reason})`;
  if (event.code) return `audio ingress disconnected (${event.code})`;
  return fallback ?? "audio ingress disconnected";
}
